import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser,
  faEdit,
  faSave,
  faHistory,
  faCog,
  faSignOutAlt,
  faDownload,
  faEye,
  faCalendarAlt,
  faStar,
  faHeart,
  faUsers,
  faBrain,
  faStethoscope,
  faRocket,
  faSpinner,
  faCamera,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faBirthdayCake,
  faLanguage,
  faBell,
  faShield,
  faTrash
} from '@fortawesome/free-solid-svg-icons';

interface AnalysisHistory {
  id: string;
  type: 'personal' | 'relationship' | 'psychological' | 'astro' | 'healing';
  title: string;
  date: string;
  status: 'completed' | 'processing';
}

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'history' | 'settings'>('profile');
  
  const [profileData, setProfileData] = useState({
    displayName: currentUser?.displayName || '',
    email: currentUser?.email || '',
    phone: '',
    location: '',
    birthDate: '',
    bio: '',
    notifications: true,
    language: 'tr',
    privacy: 'private'
  });

  const [analysisHistory] = useState<AnalysisHistory[]>([
    {
      id: '1',
      type: 'personal',
      title: t('personalNumerology'),
      date: '2024-01-15',
      status: 'completed'
    },
    {
      id: '2',
      type: 'relationship',
      title: t('relationshipNumerology'),
      date: '2024-01-10',
      status: 'completed'
    },
    {
      id: '3',
      type: 'psychological',
      title: t('psychologicalNumerology'),
      date: '2024-01-05',
      status: 'completed'
    }
  ]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // API call to save profile data
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIsEditing(false);
      console.log('Profile saved:', profileData);
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getAnalysisIcon = (type: string) => {
    switch (type) {
      case 'personal': return faStar;
      case 'relationship': return faHeart;
      case 'psychological': return faBrain;
      case 'astro': return faRocket;
      case 'healing': return faStethoscope;
      default: return faUser;
    }
  };

  const getAnalysisColor = (type: string) => {
    switch (type) {
      case 'personal': return 'linear-gradient(135deg, #8b5cf6, #9333ea)';
      case 'relationship': return 'linear-gradient(135deg, #ec4899, #be185d)';
      case 'psychological': return 'linear-gradient(135deg, #f59e0b, #d97706)';
      case 'astro': return 'linear-gradient(135deg, #3b82f6, #1e40af)';
      case 'healing': return 'linear-gradient(135deg, #10b981, #059669)';
      default: return 'linear-gradient(135deg, #6b7280, #4b5563)';
    }
  };

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    padding: '40px 20px',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: '1200px',
    margin: '0 auto'
  };

  const headerStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '3rem'
  };

  const profileCardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '20px',
    padding: '2.5rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    marginBottom: '2rem'
  };

  const tabStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    borderBottom: '1px solid #e5e7eb'
  };

  const tabButtonStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '12px 24px',
    background: 'none',
    border: 'none',
    borderBottom: isActive ? '2px solid #8b5cf6' : '2px solid transparent',
    color: isActive ? '#8b5cf6' : '#6b7280',
    fontWeight: isActive ? '600' : '400',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '1rem'
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    background: '#fafafa',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    fontFamily: 'Inter, system-ui, sans-serif',
    color: '#374151',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const buttonStyle: React.CSSProperties = {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #8b5cf6, #9333ea)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db'
  };

  const analysisCardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    marginBottom: '1rem'
  };

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 2.5rem)', fontWeight: 'bold', color: '#374151', marginBottom: '1rem' }}>
            {t('profileTitle')}
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#6b7280', maxWidth: '600px', margin: '0 auto', lineHeight: 1.6 }}>
            {t('profileSubtitle')}
          </p>
        </div>

        {/* Profile Card */}
        <div style={profileCardStyle}>
          {/* Profile Header */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem', gap: '1.5rem' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8b5cf6, #9333ea)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              {currentUser?.photoURL ? (
                <img 
                  src={currentUser.photoURL} 
                  alt="Profile" 
                  style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <FontAwesomeIcon icon={faUser} style={{ color: 'white', fontSize: '2rem' }} />
              )}
              <button style={{
                position: 'absolute',
                bottom: '0',
                right: '0',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: '#f59e0b',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FontAwesomeIcon icon={faCamera} style={{ fontSize: '0.75rem' }} />
              </button>
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#374151', margin: '0 0 0.5rem 0' }}>
                {currentUser?.displayName || t('user')}
              </h2>
              <p style={{ color: '#6b7280', margin: '0 0 1rem 0' }}>
                {currentUser?.email}
              </p>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button 
                  style={isEditing ? secondaryButtonStyle : buttonStyle}
                  onClick={() => setIsEditing(!isEditing)}
                >
                  <FontAwesomeIcon icon={isEditing ? faUser : faEdit} />
                  {isEditing ? t('cancel') : t('edit')}
                </button>
                {isEditing && (
                  <button 
                    style={buttonStyle}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    <FontAwesomeIcon icon={isSaving ? faSpinner : faSave} 
                      style={isSaving ? { animation: 'spin 1s linear infinite' } : {}} />
                    {isSaving ? t('saving') : t('save')}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={tabStyle}>
            <button 
              style={tabButtonStyle(activeTab === 'profile')}
              onClick={() => setActiveTab('profile')}
            >
              <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.5rem' }} />
              {t('personalInfo')}
            </button>
            <button 
              style={tabButtonStyle(activeTab === 'history')}
              onClick={() => setActiveTab('history')}
            >
              <FontAwesomeIcon icon={faHistory} style={{ marginRight: '0.5rem' }} />
              {t('analysisHistory')}
            </button>
            <button 
              style={tabButtonStyle(activeTab === 'settings')}
              onClick={() => setActiveTab('settings')}
            >
              <FontAwesomeIcon icon={faCog} style={{ marginRight: '0.5rem' }} />
              {t('settings')}
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'profile' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.5rem' }} />
                  {t('fullName')}
                </label>
                <input
                  type="text"
                  name="displayName"
                  value={profileData.displayName}
                  onChange={handleInputChange}
                  style={inputStyle}
                  disabled={!isEditing}
                  placeholder={t('namePlaceholder')}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '0.5rem' }} />
                  {t('email')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleInputChange}
                  style={inputStyle}
                  disabled={true}
                  placeholder={t('emailPlaceholder')}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  <FontAwesomeIcon icon={faPhone} style={{ marginRight: '0.5rem' }} />
                  {t('phone')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleInputChange}
                  style={inputStyle}
                  disabled={!isEditing}
                  placeholder={t('phonePlaceholder')}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '0.5rem' }} />
                  {t('location')}
                </label>
                <input
                  type="text"
                  name="location"
                  value={profileData.location}
                  onChange={handleInputChange}
                  style={inputStyle}
                  disabled={!isEditing}
                  placeholder={t('locationPlaceholder')}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  <FontAwesomeIcon icon={faBirthdayCake} style={{ marginRight: '0.5rem' }} />
                  {t('birthDate')}
                </label>
                <input
                  type="date"
                  name="birthDate"
                  value={profileData.birthDate}
                  onChange={handleInputChange}
                  style={inputStyle}
                  disabled={!isEditing}
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  {t('aboutMe')}
                </label>
                <textarea
                  name="bio"
                  value={profileData.bio}
                  onChange={handleInputChange}
                  style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                  disabled={!isEditing}
                  placeholder={t('aboutPlaceholder')}
                />
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', margin: 0 }}>
                  {t('analysisHistory')} ({analysisHistory.length})
                </h3>
                <button style={secondaryButtonStyle}>
                  <FontAwesomeIcon icon={faDownload} />
                  {t('downloadAll')}
                </button>
              </div>
              
              {analysisHistory.map((analysis) => (
                <div key={analysis.id} style={analysisCardStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        background: getAnalysisColor(analysis.type),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <FontAwesomeIcon 
                          icon={getAnalysisIcon(analysis.type)} 
                          style={{ color: 'white', fontSize: '1.25rem' }} 
                        />
                      </div>
                      <div>
                        <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: '0 0 0.25rem 0' }}>
                          {analysis.title}
                        </h4>
                        <p style={{ color: '#6b7280', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          {new Date(analysis.date).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button style={secondaryButtonStyle}>
                        <FontAwesomeIcon icon={faEye} />
                        {t('view')}
                      </button>
                      <button style={secondaryButtonStyle}>
                        <FontAwesomeIcon icon={faDownload} />
                        {t('download')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'settings' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  <FontAwesomeIcon icon={faLanguage} style={{ marginRight: '0.5rem' }} />
                  {t('language')}
                </label>
                <select
                  name="language"
                  value={profileData.language}
                  onChange={handleInputChange}
                  style={inputStyle}
                >
                  <option value="tr">Türkçe</option>
                  <option value="en">English</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                  <FontAwesomeIcon icon={faShield} style={{ marginRight: '0.5rem' }} />
                  {t('privacy')}
                </label>
                <select
                  name="privacy"
                  value={profileData.privacy}
                  onChange={handleInputChange}
                  style={inputStyle}
                >
                  <option value="private">{t('private')}</option>
                  <option value="public">{t('public')}</option>
                </select>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="notifications"
                    checked={profileData.notifications}
                    onChange={handleInputChange}
                    style={{ width: 'auto' }}
                  />
                  <FontAwesomeIcon icon={faBell} />
                  {t('notifications')}
                </label>
              </div>

              <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e5e7eb', paddingTop: '2rem', marginTop: '2rem' }}>
                <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', marginBottom: '1rem' }}>
                  {t('accountActions')}
                </h4>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button 
                    style={{ ...secondaryButtonStyle, color: '#ef4444', borderColor: '#fecaca' }}
                    onClick={handleLogout}
                  >
                    <FontAwesomeIcon icon={faSignOutAlt} />
                    {t('logout')}
                  </button>
                  <button style={{ ...secondaryButtonStyle, color: '#ef4444', borderColor: '#fecaca' }}>
                    <FontAwesomeIcon icon={faTrash} />
                    {t('deleteAccount')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage; 